import {
  Component,
  ElementRef,
  Input,
  OnInit,
  AfterViewInit,
  inject,
} from '@angular/core'
import { Router } from '@angular/router'
import { Mark } from 'mark.js'

import {
  Hunt,
  HuntPackageRecord,
  HuntPackagesAdded,
  HuntTemplate,
} from '@cyborg-monorepo/hunter/common'
import { DialogRef, DialogService, SnackbarService } from '@hyperkit'

import { FeatureFlagService } from '../../../../helpers/feature-flag.service'
import {
  gatherTags,
  isHuntPackageHuntCompatible,
} from '../../../../helpers/utils'
import {
  TrackingEventEnum,
  TrackingService,
} from '../../../../tracking/tracking.service'
import { CollectionData } from '../../../research/services/collection.service'
import { SearchStateService } from '../../../research/services/search-state.service'
import { CalendlyService } from '../../services/calendly.service'
import { AddToQuickHuntDialogComponent } from '../dialogs/add-to-quick-hunt-dialog/add-to-quick-hunt-dialog.component'
import { AddToTemplateDialogComponent } from '../dialogs/add-to-template-dialog/add-to-template-dialog.component'
import { HuntModuleDialogComponent } from '../dialogs/hunt-module-dialog/hunt-module-dialog.component'
import { NewHuntDialogComponent } from '../dialogs/new-hunt-dialog/new-hunt-dialog.component'
import { NewTemplateDialogComponent } from '../dialogs/new-template-dialog/new-template-dialog.component'
import { FacetButtonItem } from '../facet-buttons/facet-buttons.component'

@Component({
  standalone: false,
  selector: 'cyborg-collection',
  templateUrl: './collection.component.html',
  styleUrls: ['./collection.component.scss'],
})
export class CollectionComponent implements OnInit, AfterViewInit {
  private myElement = inject(ElementRef)
  searchStateService = inject(SearchStateService)
  featureFlagService = inject(FeatureFlagService)
  router = inject(Router)
  private dialogService = inject(DialogService)
  calendlyService = inject(CalendlyService)
  trackingService = inject(TrackingService)

  @Input() public data: CollectionData
  @Input() public highlighting: boolean

  public contentUUID: string
  public tags: FacetButtonItem[]
  public huntPackages: Array<HuntPackageRecord>
  public huntModuleAccess = false
  public huntCompatible = false
  public readonly snack = inject(SnackbarService)

  public ngOnInit(): void {
    this.huntPackages = this.data.huntPackages
    this.tags = gatherTags(this.data.collection)
    this.huntModuleAccess = this.featureFlagService.huntModuleAccess()
    this.isHuntCompatible()
  }

  public ngAfterViewInit(): void {
    if (this.highlighting) {
      setTimeout(() => {
        this.headerHiglighting()
        this.tabHighlighting()
      }, 0)
    }
  }

  public openHuntDialog(): void {
    this.trackingService.trackEvent(
      TrackingEventEnum.COMMUNITY_HUNT_MODULE_DIALOG_OPENED,
    )
    const dialogRef: DialogRef = this.dialogService.open(
      HuntModuleDialogComponent,
      {
        panelClass: 'full-panel',
      },
    )

    dialogRef.afterClosed().subscribe((talkToCyborg: boolean) => {
      if (talkToCyborg) {
        this.talkToCyborg()
      } else {
        this.trackingService.trackEvent(
          TrackingEventEnum.COMMUNITY_HUNT_MODULE_DIALOG_ABANDONED,
        )
      }
    })
  }

  public talkToCyborg(): void {
    this.calendlyService.talkToCyborg()
  }

  private isHuntCompatible(): void {
    this.huntCompatible = this.data.huntPackages.some((huntPackage) => {
      return isHuntPackageHuntCompatible(huntPackage)
    })
  }

  public addToExistingQuickHunt(): void {
    const dialogRef: DialogRef = this.dialogService.open(
      AddToQuickHuntDialogComponent,
      { data: { huntPackages: this.data.huntPackages } },
    )

    dialogRef.afterClosed().subscribe((huntMeta: HuntPackagesAdded) => {
      if (huntMeta)
        this.snack.show(
          `${huntMeta.huntPackagesAdded} new Hunt Package${huntMeta.huntPackagesAdded !== 1 ? 's' : ''} from Collection added to Hunt:`,
          5000,
          {
            text: huntMeta.name,
            callback: () => this.router.navigate(['hunt', huntMeta.id]),
          },
        )
    })
  }

  public addToNewQuickHunt(): void {
    const dialogRef: DialogRef = this.dialogService.open(
      NewHuntDialogComponent,
      {
        data: {
          huntPackages: this.data.huntPackages,
          collection: this.data.collection,
        },
      },
    )
    dialogRef.afterClosed().subscribe((hunt: Hunt) => {
      if (hunt) {
        this.router.navigate(['hunt', hunt.id, 'workbench'])
      }
    })
  }

  public addToExistingTemplate(): void {
    const dialogRef: DialogRef = this.dialogService.open(
      AddToTemplateDialogComponent,
      { data: { huntPackages: this.data.huntPackages } },
    )

    dialogRef.afterClosed().subscribe((templateMeta: HuntPackagesAdded) => {
      if (templateMeta)
        this.snack.show(
          `${templateMeta.huntPackagesAdded} new Hunt Package${templateMeta.huntPackagesAdded !== 1 ? 's' : ''} from Collection added to Hunt Template:`,
          5000,
          {
            text: templateMeta.name,
            callback: () =>
              this.router.navigate(['hunt', 'hunt-template', templateMeta.id]),
          },
        )
    })
  }

  public addToNewTemplate(): void {
    const dialogRef: DialogRef = this.dialogService.open(
      NewTemplateDialogComponent,
      {
        data: {
          huntPackages: this.data.huntPackages,
          collection: this.data.collection,
        },
      },
    )
    dialogRef.afterClosed().subscribe((result: HuntTemplate) => {
      if (result) this.router.navigate(['hunt', 'hunt-template', result.id])
    })
  }

  public headerHiglighting(): void {
    const context =
      this.myElement.nativeElement.querySelectorAll('collection-header')
    const instance = new Mark(context)
    const term = this.searchStateService.state.value.term
    if (term) {
      instance.mark(term, {
        element: 'em',
        className: 'highlight',
      })
    }
  }

  public tabHighlighting(): void {
    const context =
      this.myElement.nativeElement.querySelectorAll('collection-details')
    const instance = new Mark(context)
    const term = this.searchStateService.state.value.term
    if (term) {
      instance.unmark({
        element: 'em',
        className: 'highlight',
      })

      instance.mark(term, {
        element: 'em',
        className: 'highlight',
      })
    }
  }
}
